// packages/shared/src/constants/error-codes.ts

/**
 * Standardized error codes for the entire platform
 * Format: DOMAIN_ACTION_REASON
 */
export const ErrorCode = {
  // Network errors (client-side)
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',

  // Authentication (AUTH_*)
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  AUTH_EMAIL_NOT_VERIFIED: 'AUTH_EMAIL_NOT_VERIFIED',
  AUTH_EMAIL_ALREADY_VERIFIED: 'AUTH_EMAIL_ALREADY_VERIFIED',
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_ACCOUNT_DISABLED: 'AUTH_ACCOUNT_DISABLED',
  AUTH_UNAUTHORIZED: 'AUTH_UNAUTHORIZED',
  AUTH_FORBIDDEN: 'AUTH_FORBIDDEN',
  AUTH_INVALID_TOKEN: 'AUTH_INVALID_TOKEN',
  AUTH_CSRF_INVALID: 'AUTH_CSRF_INVALID',

  // Authentication - Extended (AUTH_*)
  AUTH_USER_INACTIVE: 'AUTH_USER_INACTIVE',
  AUTH_INVALID_OAUTH_TOKEN: 'AUTH_INVALID_OAUTH_TOKEN',
  AUTH_OAUTH_EMAIL_MISSING: 'AUTH_OAUTH_EMAIL_MISSING',
  AUTH_INVALID_REFRESH_TOKEN: 'AUTH_INVALID_REFRESH_TOKEN',
  AUTH_REFRESH_TOKEN_EXPIRED: 'AUTH_REFRESH_TOKEN_EXPIRED',
  AUTH_REFRESH_TOKEN_REUSED: 'AUTH_REFRESH_TOKEN_REUSED',
  AUTH_INVALID_VERIFICATION_TOKEN: 'AUTH_INVALID_VERIFICATION_TOKEN',
  AUTH_VERIFICATION_TOKEN_EXPIRED: 'AUTH_VERIFICATION_TOKEN_EXPIRED',
  AUTH_INVALID_RESET_TOKEN: 'AUTH_INVALID_RESET_TOKEN',
  AUTH_RESET_TOKEN_EXPIRED: 'AUTH_RESET_TOKEN_EXPIRED',
  AUTH_SESSION_NOT_FOUND: 'AUTH_SESSION_NOT_FOUND',
  AUTH_PASSWORD_SAME_AS_CURRENT: 'AUTH_PASSWORD_SAME_AS_CURRENT',

  // User (USER_*)
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_EMAIL_EXISTS: 'USER_EMAIL_EXISTS',
  USER_PHONE_EXISTS: 'USER_PHONE_EXISTS',
  USER_INVALID_PASSWORD: 'USER_INVALID_PASSWORD',

  // Venue (VENUE_*)
  VENUE_NOT_FOUND: 'VENUE_NOT_FOUND',
  VENUE_NOT_APPROVED: 'VENUE_NOT_APPROVED',
  VENUE_CLOSED: 'VENUE_CLOSED',
  VENUE_NO_AVAILABILITY: 'VENUE_NO_AVAILABILITY',
  VENUE_INVALID_TYPE: 'VENUE_INVALID_TYPE',

  // Booking (BOOKING_*)
  BOOKING_NOT_FOUND: 'BOOKING_NOT_FOUND',
  BOOKING_SLOT_UNAVAILABLE: 'BOOKING_SLOT_UNAVAILABLE',
  BOOKING_ALREADY_CANCELLED: 'BOOKING_ALREADY_CANCELLED',
  BOOKING_CANCELLATION_NOT_ALLOWED: 'BOOKING_CANCELLATION_NOT_ALLOWED',
  BOOKING_PAYMENT_PENDING: 'BOOKING_PAYMENT_PENDING',
  BOOKING_ALREADY_COMPLETED: 'BOOKING_ALREADY_COMPLETED',
  BOOKING_CREATION_FAILED: 'BOOKING_CREATION_FAILED',
  BOOKING_LOCKED: 'BOOKING_LOCKED',
  BOOKING_NOT_CHECKED_IN: 'BOOKING_NOT_CHECKED_IN',

  // Break Management (BREAK_*)
  BREAK_TRACKING_NOT_ENABLED: 'BREAK_TRACKING_NOT_ENABLED',
  BREAK_ALREADY_ACTIVE: 'BREAK_ALREADY_ACTIVE',
  BREAK_NOT_FOUND: 'BREAK_NOT_FOUND',

  // Session Extension (EXTENSION_*)
  EXTENSION_SLOT_UNAVAILABLE: 'EXTENSION_SLOT_UNAVAILABLE',
  EXTENSION_SESSION_NOT_ACTIVE: 'EXTENSION_SESSION_NOT_ACTIVE',
  EXTENSION_BREAK_ACTIVE: 'EXTENSION_BREAK_ACTIVE',
  EXTENSION_MAX_DURATION_EXCEEDED: 'EXTENSION_MAX_DURATION_EXCEEDED',

  // Payment (PAYMENT_*)
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  PAYMENT_TIMEOUT: 'PAYMENT_TIMEOUT',
  PAYMENT_CANCELLED: 'PAYMENT_CANCELLED',
  PAYMENT_INVALID_AMOUNT: 'PAYMENT_INVALID_AMOUNT',
  PAYMENT_REFUND_FAILED: 'PAYMENT_REFUND_FAILED',
  PAYMENT_NOT_FOUND: 'PAYMENT_NOT_FOUND',
  PAYMENT_AMOUNT_MISMATCH: 'PAYMENT_AMOUNT_MISMATCH',
  PAYMENT_INVALID_MERCHANT: 'PAYMENT_INVALID_MERCHANT',
  PAYMENT_QR_GENERATION_FAILED: 'PAYMENT_QR_GENERATION_FAILED',
  PAYMENT_COLLECT_PAY_FAILED: 'PAYMENT_COLLECT_PAY_FAILED',
  PAYMENT_STATUS_QUERY_FAILED: 'PAYMENT_STATUS_QUERY_FAILED',
  PAYMENT_CALLBACK_DECRYPTION_FAILED: 'PAYMENT_CALLBACK_DECRYPTION_FAILED',
  PAYMENT_CALLBACK_PROCESSING_FAILED: 'PAYMENT_CALLBACK_PROCESSING_FAILED',
  PAYMENT_UPI_ID_REQUIRED: 'PAYMENT_UPI_ID_REQUIRED',
  PAYMENT_TRANSACTION_REF_MISSING: 'PAYMENT_TRANSACTION_REF_MISSING',
  PAYMENT_TRANSACTION_REF_INVALID: 'PAYMENT_TRANSACTION_REF_INVALID',
  PAYMENT_VERIFICATION_FAILED: 'PAYMENT_VERIFICATION_FAILED',
  PAYMENT_RETRY_LIMIT_EXCEEDED: 'PAYMENT_RETRY_LIMIT_EXCEEDED',
  PAYMENT_RETRY_IN_PROGRESS: 'PAYMENT_RETRY_IN_PROGRESS',
  PAYMENT_RETRY_FAILED: 'PAYMENT_RETRY_FAILED',

  // Subscription (SUBSCRIPTION_*)
  SUBSCRIPTION_NOT_FOUND: 'SUBSCRIPTION_NOT_FOUND',
  SUBSCRIPTION_PLAN_NOT_FOUND: 'SUBSCRIPTION_PLAN_NOT_FOUND',

  // Mandate (MANDATE_*)
  MANDATE_NOT_FOUND: 'MANDATE_NOT_FOUND',
  MANDATE_REGISTRATION_FAILED: 'MANDATE_REGISTRATION_FAILED',
  MANDATE_NOT_ACTIVE: 'MANDATE_NOT_ACTIVE',
  CARD_SI_LIMIT_EXCEEDED: 'CARD_SI_LIMIT_EXCEEDED',

  // Wallet (WALLET_*)
  WALLET_NOT_FOUND: 'WALLET_NOT_FOUND',
  WALLET_INSUFFICIENT_BALANCE: 'WALLET_INSUFFICIENT_BALANCE',
  WALLET_NEGATIVE_BALANCE: 'WALLET_NEGATIVE_BALANCE',
  WALLET_TRANSACTION_FAILED: 'WALLET_TRANSACTION_FAILED',
  WALLET_CREDIT_EXPIRED: 'WALLET_CREDIT_EXPIRED',
  WALLET_CREATION_FAILED: 'WALLET_CREATION_FAILED',

  // Vendor (VENDOR_*)
  VENDOR_NOT_FOUND: 'VENDOR_NOT_FOUND',
  VENDOR_NOT_APPROVED: 'VENDOR_NOT_APPROVED',
  VENDOR_ALREADY_EXISTS: 'VENDOR_ALREADY_EXISTS',

  // Coupon (COUPON_*)
  COUPON_NOT_FOUND: 'COUPON_NOT_FOUND',
  COUPON_INVALID: 'COUPON_INVALID',
  COUPON_EXPIRED: 'COUPON_EXPIRED',
  COUPON_USAGE_LIMIT_REACHED: 'COUPON_USAGE_LIMIT_REACHED',
  COUPON_NOT_APPLICABLE: 'COUPON_NOT_APPLICABLE',
  COUPON_MINIMUM_NOT_MET: 'COUPON_MINIMUM_NOT_MET',

  // Review (REVIEW_*)
  REVIEW_NOT_FOUND: 'REVIEW_NOT_FOUND',
  REVIEW_ALREADY_EXISTS: 'REVIEW_ALREADY_EXISTS',
  REVIEW_NOT_ALLOWED: 'REVIEW_NOT_ALLOWED',

  // Favorite (FAVORITE_*)
  FAVORITE_NOT_FOUND: 'FAVORITE_NOT_FOUND',
  FAVORITE_ALREADY_EXISTS: 'FAVORITE_ALREADY_EXISTS',

  // Staff (STAFF_*)
  STAFF_NOT_FOUND: 'STAFF_NOT_FOUND',
  STAFF_INVITATION_EXPIRED: 'STAFF_INVITATION_EXPIRED',
  STAFF_INVITATION_INVALID: 'STAFF_INVITATION_INVALID',
  STAFF_ALREADY_EXISTS: 'STAFF_ALREADY_EXISTS',

  // Inventory (INVENTORY_*)
  INVENTORY_NOT_FOUND: 'INVENTORY_NOT_FOUND',
  INVENTORY_UNAVAILABLE: 'INVENTORY_UNAVAILABLE',
  INVENTORY_REQUEST_PENDING: 'INVENTORY_REQUEST_PENDING',

  // Addon Pricing (ADDON_*)
  ADDON_PRICING_NOT_CONFIGURED: 'ADDON_PRICING_NOT_CONFIGURED',

  // Bundle (BUNDLE_*)
  BUNDLE_NOT_FOUND: 'BUNDLE_NOT_FOUND',
  BUNDLE_NO_DISCOUNT: 'BUNDLE_NO_DISCOUNT',

  // Notification (NOTIFICATION_*)
  NOTIFICATION_NOT_FOUND: 'NOTIFICATION_NOT_FOUND',

  // Payout (PAYOUT_*)
  PAYOUT_NOT_FOUND: 'PAYOUT_NOT_FOUND',
  PAYOUT_INVALID_STATUS: 'PAYOUT_INVALID_STATUS',
  PAYOUT_NO_ELIGIBLE_BOOKINGS: 'PAYOUT_NO_ELIGIBLE_BOOKINGS',
  PAYOUT_BANK_ACCOUNT_NOT_FOUND: 'PAYOUT_BANK_ACCOUNT_NOT_FOUND',
  PAYOUT_PROCESSING_FAILED: 'PAYOUT_PROCESSING_FAILED',
  PAYOUT_INVALID_AMOUNT: 'PAYOUT_INVALID_AMOUNT',
  PAYOUT_INVALID_STATUS_TRANSITION: 'PAYOUT_INVALID_STATUS_TRANSITION',

  // Upload (UPLOAD_*)
  UPLOAD_FILE_TOO_LARGE: 'UPLOAD_FILE_TOO_LARGE',
  UPLOAD_INVALID_FORMAT: 'UPLOAD_INVALID_FORMAT',
  UPLOAD_FAILED: 'UPLOAD_FAILED',

  // Validation (VALIDATION_*)
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  VALIDATION_REQUIRED_FIELD: 'VALIDATION_REQUIRED_FIELD',
  VALIDATION_INVALID_FORMAT: 'VALIDATION_INVALID_FORMAT',

  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',

  // Server errors
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR: 'DATABASE_ERROR',

  // Resource errors
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  RESOURCE_GONE: 'RESOURCE_GONE',

  // Operation errors
  OPERATION_NOT_ALLOWED: 'OPERATION_NOT_ALLOWED',

  // Config errors
  VENUE_TYPE_CONFIG_NOT_FOUND: 'VENUE_TYPE_CONFIG_NOT_FOUND',
  VENUE_TYPE_CONFIG_CODE_EXISTS: 'VENUE_TYPE_CONFIG_CODE_EXISTS',
  INVALID_VENUE_TYPE: 'INVALID_VENUE_TYPE',
  AMENITY_CONFIG_NOT_FOUND: 'AMENITY_CONFIG_NOT_FOUND',
  AMENITY_CONFIG_CODE_EXISTS: 'AMENITY_CONFIG_CODE_EXISTS',
  INVENTORY_CATEGORY_CONFIG_NOT_FOUND: 'INVENTORY_CATEGORY_CONFIG_NOT_FOUND',
  INVENTORY_CATEGORY_CONFIG_CODE_EXISTS: 'INVENTORY_CATEGORY_CONFIG_CODE_EXISTS',
  ALLERGEN_CONFIG_NOT_FOUND: 'ALLERGEN_CONFIG_NOT_FOUND',
  ALLERGEN_CONFIG_CODE_EXISTS: 'ALLERGEN_CONFIG_CODE_EXISTS',
  SPICE_LEVEL_CONFIG_NOT_FOUND: 'SPICE_LEVEL_CONFIG_NOT_FOUND',
  SPICE_LEVEL_CONFIG_LEVEL_EXISTS: 'SPICE_LEVEL_CONFIG_LEVEL_EXISTS',

  // Feature management
  FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',

  // Referral
  REFERRAL_NOT_CONFIGURED: 'REFERRAL_NOT_CONFIGURED',

  // Agent / PC unlock
  AGENT_SIGNATURE_INVALID: 'AGENT_SIGNATURE_INVALID',
  AGENT_QR_EXPIRED: 'AGENT_QR_EXPIRED',
  AGENT_UNIT_NOT_FOUND: 'AGENT_UNIT_NOT_FOUND',
  AGENT_UNIT_NOT_IDLE: 'AGENT_UNIT_NOT_IDLE',
  AGENT_PAYMENT_NOT_FOUND: 'AGENT_PAYMENT_NOT_FOUND',
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/**
 * Error codes that should preserve server messages instead of using predefined messages.
 * These have dynamic content (e.g., remaining lockout time, validation details).
 */
export const PRESERVE_SERVER_MESSAGE_CODES = new Set<ErrorCodeValue>([
  ErrorCode.VALIDATION_ERROR,
  ErrorCode.AUTH_ACCOUNT_LOCKED,
  ErrorCode.RESOURCE_CONFLICT,
  // Add future codes here that need dynamic server messages
]);
